import { AnimatedContentLoader } from '@components'
import { useBusinessContext } from '@context'
import { IDocument, useInvalidateDocumentLimits, useInvalidateTrialLimits } from '@query'
import {
  acceptAccountingSuggestion,
  fetchAccountingSuggestions,
  IAccountingSuggestionAcceptPayload
} from '@query/suggestions'
import React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { SuggestionCardStack } from './SuggestionCardStack'
import { useInterval } from '@hooks/useInterval.ts'
import { useIsPresent } from 'framer-motion'

export const AccountingSuggestions: React.FC<{
  document: IDocument
  emptyContent?: React.ReactNode
}> = ({ document, emptyContent }) => {
  const queryClient = useQueryClient()
  const { slug } = useBusinessContext()
  const invalidateDocumentLimits = useInvalidateDocumentLimits()
  const invalidateTrialLimits = useInvalidateTrialLimits()
  const isPresent = useIsPresent()

  const { data, isLoading } = useQuery({
    queryKey: [slug, 'suggestions', document.id],
    queryFn: () => fetchAccountingSuggestions({ slug, documentId: document.id })
  })
  const suggestions = data?.results || []

  // Refetch related queries if the document is being updated by the worker
  useInterval(
    () => {
      queryClient.invalidateQueries({ queryKey: [slug, 'documents', document?.id] })
      queryClient.invalidateQueries({ queryKey: [slug, 'suggestions', document.id] })
    },
    {
      enabled:
        isPresent &&
        (document?.import_data?.algo_status == 'PENDING' ||
          document?.import_data?.algo_status == 'RUNNING'),
      interval: 5000
    }
  )

  const invalidateQueries = React.useCallback(async () => {
    // Invalidate document limits
    invalidateDocumentLimits(document.date)
    invalidateTrialLimits()

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [slug, 'documents'] }),
      queryClient.invalidateQueries({ queryKey: [slug, 'documents', document.id] }),
      queryClient.invalidateQueries({ queryKey: [slug], exact: true })
    ])
    // Wait until other queries are invalidated before invalidating suggestions
    // as it keeps screen showing unupdated document data
    await queryClient.invalidateQueries({ queryKey: [slug, 'suggestions', document.id] })
  }, [queryClient, document.id])

  interface AcceptPropsIn {
    suggestionId: number
    modifiedData?: Partial<IAccountingSuggestionAcceptPayload>
  }

  const acceptMutation = useMutation<unknown, unknown, AcceptPropsIn>({
    mutationFn: ({ suggestionId, modifiedData }) => {
      return acceptAccountingSuggestion({ slug, suggestionId, modifiedData })
    },
    ...{
      onSuccess: async () => {
        await invalidateQueries()
      }
    }
  })

  return (
    <Wrapper>
      <AnimatedContentLoader
        isLoading={isLoading}
        isEmpty={suggestions.length === 0}
        isEmptyContent={emptyContent}
      >
        {suggestions.length > 0 && (
          <SuggestionCardStack
            suggestions={suggestions}
            document={document}
            onAccept={(suggestionId, modifiedData) => {
              return acceptMutation.mutateAsync({ suggestionId, modifiedData })
            }}
          />
        )}
      </AnimatedContentLoader>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`
